import { createPublicationStateTransaction } from '../orchestration/publicationStateTransaction';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { callBackend } from '../../../utils/api';
import { normalizeBetaSource } from '../../../utils/betaSources';
import { normalizeIdioVolSource } from '../dashboardUtils';
import { normalizeUserFactorModel } from '../../../utils/factorDataOptions';
import {
    buildRiskRequestKey,
    runInFlightRequest,
} from '../orchestration/requestRegistry';
import { getRiskCommandsOrThrow } from '../orchestration/riskCommandRef';
import { RISK_INVALIDATION_CAUSES } from '../orchestration/useRiskOrchestrationCommands';
import { isValidPosSizingPublicationBase } from '../../../utils/posSizingPublicationContract';

const samePublicationBase = (first, second) => ['portfolio_id', 'portfolio_row_version', 'watchlist_revision_id']
    .every(key => first?.[key] === second?.[key]);
const sameChildPublication = (first, second) => ['portfolio_id', 'portfolio_row_version']
    .every(key => first?.[key] === second?.[key]);

function createRequestId() {
    if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
    const bytes = typeof globalThis.crypto?.getRandomValues === 'function'
        ? globalThis.crypto.getRandomValues(new Uint8Array(16))
        : Uint8Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const obsoleteResult = () => ({ success: false, obsolete: true });
const finishWaiters = (queued, result) => {
    queued?.waiters.forEach(resolve => resolve(result));
    if (queued) queued.waiters = [];
};
const retireScope = (scope, inFlightRef) => {
    if (!scope) return;
    scope.active = false;
    scope.epoch += 1;
    if (inFlightRef.current === scope.activeRun?.completion) inFlightRef.current = false;
    finishWaiters(scope.pending, obsoleteResult());
    finishWaiters(scope.activeRun?.queued, obsoleteResult());
    scope.pending = null;
    scope.activeRun = null;
};
const requestStillCurrent = queued => typeof queued?.isCurrentRequest !== 'function' || queued.isCurrentRequest();
const logOverviewRefreshError = error => console.error('Position Sizing overview follow-up failed:', error);

export default function usePosSizingSync({
    userUid,
    sessionScopeKey,
    activeView,
    selectedFactorModel,
    requestedFactorDataModel,
    factorSnapshotDate,
    selectedBetaSource,
    posSizingIdioVolSource,
    posSizingLoading,
    posSizingAutoSyncEnabled,
    autoSyncPreferencesReady = true,
    reconcilePosSizingContext,
    posSizingPublicationBaseRef,
    syncStatusBootstrapEnabled = false,
    actualPortfolioVersion = 0,
    importApplyInFlightRef,
    exclusiveCoreLoadOwnerRef,
    onActualSyncError,
    riskCommandsRef,
    setPosSizingSyncStatus,
    setPosSizingSyncStatusLoading,
    setPendingPosSizingAutoSync,
    setPosSizingFreshnessDetailsOpen,
    setPosSizingDraftSyncLoading,
    setPosSizingTableSaving,
    posSizingSyncStatusRequestIdRef,
    posSizingAutoSyncEnabledRef,
    posSizingSyncInFlightRef,
    posSizingEditingRef,
    posSizingSavingRef,
    posSizingPendingChangesRef,
}) {
    if (typeof sessionScopeKey !== 'string' || !sessionScopeKey.trim()) {
        throw new Error('Position Sizing requires a nonblank session scope key.');
    }
    const [contextUpdateState, setContextUpdateState] = useState({ phase: 'checking' });
    const pumpRef = useRef(null);
    const runtimeRef = useRef(null);
    const lastViewRef = useRef(null);
    const bootstrapOwnerRef = useRef(null);
    const draftLoadingOwnerRef = useRef(null);
    const statusLoadingOwnerRef = useRef(null);
    const scopeKey = sessionScopeKey;
    if (!bootstrapOwnerRef.current || bootstrapOwnerRef.current.userUid !== userUid
        || bootstrapOwnerRef.current.scopeKey !== scopeKey) {
        retireScope(bootstrapOwnerRef.current, posSizingSyncInFlightRef);
        bootstrapOwnerRef.current = { userUid, scopeKey, active: true, epoch: 0,
            requestScopeKey: createRequestId(), pending: null, activeRun: null,
            readRecovery: null, externalReadPending: false,
            publicationAttempt: null, overviewRefreshPending: false,
            contextState: { phase: 'checking' }, statusAccepted: false };
    }
    const scope = bootstrapOwnerRef.current;
    // Reset departed-session presentation before async reads or later layout
    // consumers can publish this session's validated workspace.
    useLayoutEffect(() => {
        scope.active = true;
        setContextUpdateState(scope.contextState);
        runtimeRef.current.setPendingPosSizingAutoSync(scope.pending);
        if (!scope.statusAccepted) runtimeRef.current.setPosSizingSyncStatus(null);
        if (draftLoadingOwnerRef.current && (draftLoadingOwnerRef.current.scope !== scope
            || draftLoadingOwnerRef.current.epoch !== scope.epoch)) {
            draftLoadingOwnerRef.current = null;
            runtimeRef.current.setPosSizingDraftSyncLoading(false);
        }
        if (statusLoadingOwnerRef.current && (statusLoadingOwnerRef.current.scope !== scope
            || statusLoadingOwnerRef.current.epoch !== scope.epoch)) {
            statusLoadingOwnerRef.current = null;
            runtimeRef.current.setPosSizingSyncStatusLoading(false);
        }
        return () => { retireScope(scope, posSizingSyncInFlightRef); };
    }, [scope, posSizingSyncInFlightRef]);
    const fetchPosSizingSyncStatus = useCallback(async (overrides = {}) => {
        const owner = scope;
        const epoch = owner.epoch;
        const staging = typeof overrides.stagePublicationState === 'function';
        const isCurrentScope = () => owner.active && bootstrapOwnerRef.current === owner && owner.epoch === epoch;
        const isCurrent = () => isCurrentScope()
            && (!exclusiveCoreLoadOwnerRef?.current || staging)
            && (typeof overrides.isCurrentRequest !== 'function' || overrides.isCurrentRequest());
        if (!userUid || !isCurrent()) return null;
        if (staging && exclusiveCoreLoadOwnerRef?.current && typeof overrides.isCurrentRequest !== 'function') {
            throw new Error('Prepared child status requires its exact publication owner fence.');
        }
        const requestId = ++posSizingSyncStatusRequestIdRef.current;
        const resolvedFactorModel =
            Object.prototype.hasOwnProperty.call(overrides, 'factorModel')
                ? overrides.factorModel
                : selectedFactorModel;
        const resolvedFactorDataModel =
            Object.prototype.hasOwnProperty.call(overrides, 'factorDataModel')
                ? overrides.factorDataModel
                : requestedFactorDataModel;
        const resolvedFactorSnapshotDate =
            Object.prototype.hasOwnProperty.call(overrides, 'factorSnapshotDate')
                ? overrides.factorSnapshotDate
                : factorSnapshotDate;
        const resolvedBetaSource =
            Object.prototype.hasOwnProperty.call(overrides, 'betaSource')
                ? overrides.betaSource
                : selectedBetaSource;
        const resolvedIdioVolSource =
            Object.prototype.hasOwnProperty.call(overrides, 'idioVolSource')
                ? overrides.idioVolSource
                : posSizingIdioVolSource;
        const normalizedFactorModel = normalizeUserFactorModel(resolvedFactorModel);
        const normalizedBetaSource = normalizeBetaSource(resolvedBetaSource);
        const normalizedIdioVolSource = normalizeIdioVolSource(resolvedIdioVolSource);
        const forceFresh = Boolean(overrides?.forceFresh);
        const singleFlightKey = buildRiskRequestKey([
            'fetch_pos_sizing_sync_status',
            userUid,
            scopeKey,
            owner.requestScopeKey,
            epoch,
            normalizedFactorModel,
            resolvedFactorDataModel ?? '',
            resolvedFactorSnapshotDate ?? '',
            normalizedBetaSource,
            normalizedIdioVolSource,
            forceFresh ? requestId : 'shared',
        ]);

        const loadingOwner = { scope: owner, epoch };
        statusLoadingOwnerRef.current = loadingOwner;
        const present = apply => overrides.stagePublicationState
            ? overrides.stagePublicationState(apply, isCurrent)
            : apply();
        present(() => setPosSizingSyncStatusLoading(true));
        try {
            const result = await runInFlightRequest(singleFlightKey, () => callBackend(
                'fetch_pos_sizing_sync_status',
                'POST',
                {
                    factor_model: normalizedFactorModel,
                    factor_data_model: resolvedFactorDataModel,
                    factor_snapshot_date: resolvedFactorSnapshotDate,
                    beta_source: normalizedBetaSource,
                    idio_vol_source: normalizedIdioVolSource,
                },
            ));
            if (!isCurrent() || posSizingSyncStatusRequestIdRef.current !== requestId) {
                return result ?? null;
            }
            owner.statusAccepted = true;
            present(() => setPosSizingSyncStatus(result ?? null));
            return result;
        } catch (error) {
            if (!isCurrent() || posSizingSyncStatusRequestIdRef.current !== requestId) {
                return null;
            }
            // Required preparation must retain the actual failed request.
            if (staging) throw error;
            console.error('Error fetching pos-sizing sync status:', error);
            owner.statusAccepted = true;
            present(() => setPosSizingSyncStatus(null));
            return null;
        } finally {
            if (isCurrentScope() && statusLoadingOwnerRef.current === loadingOwner) {
                statusLoadingOwnerRef.current = null;
                if (!exclusiveCoreLoadOwnerRef?.current || staging) {
                    present(() => setPosSizingSyncStatusLoading(false));
                }
            }
        }
    }, [
        scope,
        scopeKey,
        exclusiveCoreLoadOwnerRef,
        factorSnapshotDate,
        posSizingIdioVolSource,
        posSizingSyncStatusRequestIdRef,
        requestedFactorDataModel,
        selectedBetaSource,
        selectedFactorModel,
        setPosSizingSyncStatus,
        setPosSizingSyncStatusLoading,
        userUid,
    ]);

    runtimeRef.current = {
        userUid, fetchPosSizingSyncStatus, reconcilePosSizingContext,
        posSizingPublicationBaseRef,
        autoSyncPreferencesReady, posSizingAutoSyncEnabledRef,
        posSizingLoading, importApplyInFlightRef, exclusiveCoreLoadOwnerRef,
        riskCommandsRef, setPosSizingDraftSyncLoading, setPosSizingSyncStatusLoading, setPosSizingSyncStatus,
        setPendingPosSizingAutoSync, setPosSizingFreshnessDetailsOpen,
        posSizingEditingRef, posSizingSavingRef, posSizingPendingChangesRef,
        onActualSyncError,
    };

    const publishState = useCallback((owner, state, queued = null) => {
        if (owner.active && bootstrapOwnerRef.current === owner) {
            owner.contextState = state;
            const apply = () => setContextUpdateState(state);
            if (queued?.stagePublicationState) queued.stagePublicationState(apply,
                () => owner.active && bootstrapOwnerRef.current === owner && requestStillCurrent(queued));
            else apply();
        }
    }, []);

    const pump = useCallback(() => {
        const current = runtimeRef.current;
        const owner = bootstrapOwnerRef.current;
        const queued = owner.pending;
        const runPresentation = queued?.stagePublicationState ? createPublicationStateTransaction() : null;
        const stageRunState = runPresentation?.stage ?? queued?.stagePublicationState;
        const present = apply => stageRunState
            ? stageRunState(apply, () => owner.active && bootstrapOwnerRef.current === owner && requestStillCurrent(queued))
            : apply();
        const publishRunState = state => publishState(owner, state,
            stageRunState ? { ...queued, stagePublicationState: stageRunState } : queued);
        if (!current.userUid || !owner.active || !queued || owner.activeRun) return;
        if (!requestStillCurrent(queued)) {
            owner.pending = null;
            present(() => runtimeRef.current.setPendingPosSizingAutoSync(null));
            finishWaiters(queued, obsoleteResult());
            return;
        }
        const isActualHandoff = queued.acceptance === 'actual_change';
        const optionalEditingDeferred = !isActualHandoff && (
            current.posSizingEditingRef.current || current.posSizingSavingRef.current
            || current.posSizingPendingChangesRef.current
        );
        if (queued.allowParentPublication && optionalEditingDeferred) {
            // The parent does not own acceptance of an optional edited draft.
            // Complete only that policy decision, preserving its saved data;
            // the normal context effect can recheck after the parent reveal.
            owner.pending = null;
            publishRunState({ phase: 'awaiting_acceptance', reasons: queued.reasons });
            present(() => runtimeRef.current.setPendingPosSizingAutoSync(null));
            runPresentation?.transferTo(queued.stagePublicationState);
            finishWaiters(queued, { success: true, deferred: true, awaitingAcceptance: true });
            return;
        }
        if (current.posSizingLoading
            || (!queued.allowParentPublication && (
                current.importApplyInFlightRef?.current
                || current.exclusiveCoreLoadOwnerRef?.current
            ))
            || (!isActualHandoff && (
                current.posSizingEditingRef.current
                || current.posSizingSavingRef.current
                || current.posSizingPendingChangesRef.current
            ))) {
            publishRunState({ phase: 'deferred', reasons: queued.reasons });
            return;
        }
        const epoch = owner.epoch;
        const isCurrentScope = () => owner.active && bootstrapOwnerRef.current === owner && owner.epoch === epoch;
        const isCurrent = () => isCurrentScope() && requestStillCurrent(queued);
        const loadingOwner = { scope: owner, epoch };
        owner.pending = null;
        const run = async () => {
            let result;
            let preparedWorkspace = null;
            let preparedSummaries = null;
            let didPublish = false;
            const commands = () => getRiskCommandsOrThrow(
                runtimeRef.current.riskCommandsRef, 'usePosSizingSync.contextUpdate',
                ['invalidateRiskSurface', 'refreshPosSizingContext',
                    ...(!queued.allowParentPublication ? ['refreshSelectedOverview'] : [])],
            );
            try {
                publishRunState({ phase: owner.readRecovery ? 'loading_committed' : 'checking', reasons: queued.reasons });
                const recovery = owner.readRecovery;
                let publication = recovery;
                if (!recovery) {
                    const unresolvedAttempt = owner.publicationAttempt;
                    const status = unresolvedAttempt ? null : await runtimeRef.current.fetchPosSizingSyncStatus({
                        forceFresh: true,
                        isCurrentRequest: isCurrent,
                        ...(queued.preparedTarget?.factorContext ?? {}),
                        ...(queued.stagePublicationState ? { stagePublicationState: stageRunState } : {}),
                        ...(queued.idioVolSource ? { idioVolSource: queued.idioVolSource } : {}),
                    });
                    if (!isCurrent()) return { success: false, obsolete: true };
                    let decision = unresolvedAttempt?.decision ?? status?.context_update;
                    if (!decision || decision.contract !== 'pos-sizing-context-update.v1') {
                        throw new Error('Unable to verify the current Position Sizing parent context.');
                    }
                    const policyFor = (candidate) => {
                        const manualUnknownRepair = queued.acceptance === 'manual' && candidate.state === 'unknown';
                        const required = Boolean(unresolvedAttempt || candidate.required || queued.requireRebuild || manualUnknownRepair);
                        const latest = runtimeRef.current;
                        const accepted = Boolean(unresolvedAttempt || queued.acceptance === 'manual' || candidate.mandatory
                            || (!queued.checkOnly && candidate.automatic_eligible !== false
                                && latest.autoSyncPreferencesReady && latest.posSizingAutoSyncEnabledRef.current));
                        const canExecute = required && accepted && (candidate.state !== 'unknown'
                            || queued.requireRebuild || manualUnknownRepair || candidate.mandatory);
                        return { manualUnknownRepair, required, accepted, canExecute };
                    };
                    const localBase = runtimeRef.current.posSizingPublicationBaseRef?.current;
                    if (!unresolvedAttempt && !policyFor(decision).canExecute
                        && isValidPosSizingPublicationBase(decision.publication_base)
                        && (owner.externalReadPending || (localBase?.portfolio_id != null
                            && isValidPosSizingPublicationBase(localBase)
                            && !sameChildPublication(localBase, decision.publication_base)))) {
                        // Another session can publish an already-current child.
                        // Adopt that saved workspace without publishing it again.
                        // A cold workspace still uses its existing lazy consumer.
                        owner.externalReadPending = true;
                        publishRunState({ phase: 'loading_committed' });
                        const adopted = await commands().refreshPosSizingContext({
                            reason: queued.reason, fetchData: true, persistPortfolio: false,
                            includeSyncStatus: false, refreshOptimizerSummaries: true,
                            isCurrentRequest: isCurrent,
                            matricesOverride: queued.preparedTarget?.factorContext ?? queued.matricesOverride,
                            preparedTarget: queued.preparedTarget,
                            stagePublicationState: stageRunState,
                        });
                        if (!isCurrent()) return { success: false, obsolete: true };
                        const workspace = adopted?.result;
                        const adoptedStatus = workspace?.pos_sizing_sync_status;
                        const adoptedDecision = adoptedStatus?.context_update;
                        const adoptedSummaries = adopted?.optimizerSummaries;
                        if (workspace?._workspace_verified !== true
                            || !isValidPosSizingPublicationBase(workspace.publication_base)
                            || adoptedDecision?.contract !== 'pos-sizing-context-update.v1'
                            || !samePublicationBase(workspace.publication_base, adoptedDecision.publication_base)) {
                            throw new Error('The current saved draft could not be loaded and verified. Retry loading.');
                        }
                        if (!Array.isArray(adoptedSummaries?.portfolios)
                            && !(adoptedSummaries?.status === 'skipped' && adoptedSummaries?.reason === 'empty_watchlist')) {
                            throw new Error('The current saved draft loaded, but the portfolio list could not be refreshed. Retry loading.');
                        }
                        if (stageRunState && typeof queued.preparedTarget?.registerDiscard === 'function') {
                            const base = workspace.publication_base;
                            queued.preparedTarget.registerDiscard(() => {
                                if (owner.active && bootstrapOwnerRef.current === owner && owner.epoch === epoch
                                    && samePublicationBase(runtimeRef.current.posSizingPublicationBaseRef?.current, base)) {
                                    owner.externalReadPending = true;
                                }
                            });
                        }
                        preparedWorkspace = workspace;
                        preparedSummaries = adoptedSummaries;
                        decision = adoptedDecision;
                        owner.externalReadPending = false;
                        owner.overviewRefreshPending = true;
                    }
                    const { manualUnknownRepair, required, accepted } = policyFor(decision);
                    if (!required) {
                        publishRunState({ phase: decision.state === 'unknown' ? 'waiting_for_data' : 'current', decision });
                        return { success: true, unchanged: true,
                            ...(preparedWorkspace ? { workspace: preparedWorkspace, optimizerSummaries: preparedSummaries } : {}) };
                    }
                    if (decision.state === 'unknown' && !queued.requireRebuild && !manualUnknownRepair && !decision.mandatory) {
                        publishRunState({ phase: 'waiting_for_data', decision });
                        return { success: false, message: 'Position Sizing context could not be fully compared.' };
                    }
                    const latest = runtimeRef.current;
                    if (!accepted) {
                        publishRunState({ phase: 'awaiting_acceptance', decision });
                        return { success: true, awaitingAcceptance: true };
                    }
                    const acceptance = unresolvedAttempt?.acceptance ?? (decision.mandatory ? 'actual_change'
                        : queued.acceptance === 'manual' ? 'manual' : 'automatic');
                    publishRunState({ phase: 'updating', decision });
                    draftLoadingOwnerRef.current = loadingOwner;
                    present(() => runtimeRef.current.setPosSizingDraftSyncLoading(true));
                    present(() => runtimeRef.current.setPosSizingFreshnessDetailsOpen(false));
                    commands().invalidateRiskSurface({
                        cause: RISK_INVALIDATION_CAUSES.POS_SIZING_RESULT_CHANGED,
                        reason: queued.reason,
                        preserveVisibleState: Boolean(queued.stagePublicationState),
                    });
                    if (typeof latest.reconcilePosSizingContext !== 'function') {
                        throw new Error('Position Sizing context publication is unavailable.');
                    }
                    const attempt = unresolvedAttempt ?? {
                        acceptance, contextUpdate: decision,
                        decision,
                        matricesOverride: queued.matricesOverride,
                        idioVolSource: queued.idioVolSource,
                        requireRebuild: Boolean(queued.requireRebuild || manualUnknownRepair),
                        requestId: queued.requestId,
                    };
                    owner.publicationAttempt = attempt;
                    publication = await latest.reconcilePosSizingContext({
                        ...attempt,
                        preparedTarget: queued.preparedTarget,
                        stagePublicationState: stageRunState,
                        isCurrentRequest: isCurrent,
                    });
                    if (!isCurrent()) return { success: false, obsolete: true };
                    if (!publication?.success) throw new Error('Position Sizing could not publish its context update.');
                    owner.publicationAttempt = null;
                    if (publication.unchanged) {
                        const finalDecision = publication.context_update;
                        if (finalDecision?.contract !== 'pos-sizing-context-update.v1') {
                            throw new Error('Position Sizing could not verify the update outcome. Retry checking its context.');
                        }
                        const awaitingAcceptance = finalDecision.state !== 'unknown' && finalDecision.required;
                        publishRunState({ phase: finalDecision.state === 'unknown' ? 'waiting_for_data'
                            : awaitingAcceptance ? 'awaiting_acceptance' : 'current', decision: finalDecision });
                        return { success: true, unchanged: true, ...(awaitingAcceptance ? { awaitingAcceptance: true } : {}) };
                    }
                    owner.externalReadPending = false;
                    owner.readRecovery = publication;
                    didPublish = true;
                }
                publishRunState({ phase: 'loading_committed' });
                const hydrated = await commands().refreshPosSizingContext({
                    reason: queued.reason, fetchData: true, persistPortfolio: false,
                    includeSyncStatus: false, refreshOptimizerSummaries: true,
                    matricesOverride: queued.preparedTarget?.factorContext ?? queued.matricesOverride,
                    preparedTarget: queued.preparedTarget,
                    stagePublicationState: stageRunState,
                    isCurrentRequest: isCurrent,
                });
                if (!isCurrent()) return { success: false, obsolete: true };
                const expected = publication.receipt?.publication_base;
                const actual = hydrated?.result?.publication_base;
                const latestDecision = hydrated?.result?.pos_sizing_sync_status?.context_update;
                if (queued.stagePublicationState && typeof queued.preparedTarget?.registerDiscard === 'function'
                    && actual && hydrated?.result?._workspace_verified === true) {
                    queued.preparedTarget.registerDiscard(() => {
                        if (owner.active && bootstrapOwnerRef.current === owner && owner.epoch === epoch
                            && samePublicationBase(runtimeRef.current.posSizingPublicationBaseRef?.current, actual)) {
                            // A verified durable workspace was not revealed.
                            // Reuse the existing adoption read path next time;
                            // never replay its already committed publication.
                            owner.externalReadPending = true;
                        }
                    });
                }
                if (latestDecision?.contract !== 'pos-sizing-context-update.v1') {
                    throw new Error('The draft was saved, but its parent context could not be verified. Retry loading.');
                }
                const summaries = hydrated?.optimizerSummaries;
                if (!Array.isArray(summaries?.portfolios)
                    && !(summaries?.status === 'skipped' && summaries?.reason === 'empty_watchlist')) {
                    throw new Error('The draft was saved, but the portfolio list could not be refreshed. Retry loading.');
                }
                if (!expected || !actual || !samePublicationBase(actual, expected)) {
                    if (actual && hydrated?.result?._workspace_verified === true) {
                        // The workspace reader has verified a subsequent
                        // authoritative publication (for example another tab).
                        // Adopt it and recompare instead of republishing the
                        // older transition or endlessly trying to reload it.
                        owner.readRecovery = null;
                        publishRunState({ phase: 'checking' });
                        return { success: true, recovered: true, adoptedPublicationBase: actual,
                            optimizerSummaries: summaries, workspace: hydrated.result };
                    }
                    throw new Error('The draft was saved, but its matching workspace could not be loaded. Retry loading.');
                }
                owner.readRecovery = null;
                const stillRequired = latestDecision?.required === true;
                const canFollow = latestDecision?.mandatory || (
                    runtimeRef.current.autoSyncPreferencesReady
                    && runtimeRef.current.posSizingAutoSyncEnabledRef.current
                    && latestDecision?.automatic_eligible !== false
                );
                publishRunState({ phase: latestDecision?.state === 'unknown' ? 'waiting_for_data'
                    : stillRequired ? (canFollow ? 'awaiting_next_check' : 'awaiting_acceptance') : 'current',
                decision: latestDecision });
                present(() => runtimeRef.current.setPosSizingFreshnessDetailsOpen(false));
                return { success: true, receipt: publication.receipt, recovered: publication.recovered === true,
                    optimizerSummaries: summaries, workspace: hydrated.result,
                    needsFollowup: stillRequired && canFollow && queued.remainingFollowups > 0 };
            } catch (error) {
                if (!isCurrent()) return obsoleteResult();
                if (Number(error?.status) >= 400 && Number(error?.status) < 500) owner.publicationAttempt = null;
                result = { success: false, message: error.message, error, committed: Boolean(owner.readRecovery) };
                publishRunState({ phase: owner.readRecovery ? 'loading_failed' : 'failed', message: error.message });
                if (isCurrent() && queued.acceptance === 'actual_change') {
                    present(() => runtimeRef.current.onActualSyncError?.({ code: 'actual_sync_incomplete', message: error.message }));
                }
                return result;
            } finally {
                if (isCurrentScope() && draftLoadingOwnerRef.current === loadingOwner) {
                    draftLoadingOwnerRef.current = null;
                    present(() => runtimeRef.current.setPosSizingDraftSyncLoading(false));
                }
                if (isCurrent() && (didPublish || owner.readRecovery)) owner.overviewRefreshPending = true;
            }
        };
        const completion = run();
        owner.activeRun = { completion, queued };
        posSizingSyncInFlightRef.current = completion;
        void completion.then((result) => {
            if (!isCurrentScope()) return;
            const followup = (result?.recovered || result?.needsFollowup) && isCurrent();
            if (runPresentation) {
                if (followup || !isCurrent()) {
                    // A verified private frame is about to be discarded for a
                    // mandatory recheck. If the child is already current, the
                    // existing adoption read must restage it before reveal.
                    if (followup && isCurrent()
                        && result?.workspace?._workspace_verified === true
                        && isValidPosSizingPublicationBase(result.workspace.publication_base)
                        && samePublicationBase(runtimeRef.current.posSizingPublicationBaseRef?.current,
                            result.workspace.publication_base)) {
                        owner.externalReadPending = true;
                    }
                    runPresentation.discard();
                } else {
                    queued.preparedTarget?.registerDiscard?.(runPresentation.discard);
                    runPresentation.transferTo(queued.stagePublicationState);
                }
            }
            if (followup) {
                // Recovery proves the old outcome. Recompare once before
                // completing any newer parent handoff waiting behind it.
                const next = owner.pending;
                owner.pending = {
                    ...queued, ...next,
                    requestId: next?.requestId ?? createRequestId(),
                    remainingFollowups: result.needsFollowup ? queued.remainingFollowups - 1 : queued.remainingFollowups,
                    waiters: [...queued.waiters, ...(next?.waiters ?? [])],
                };
            }
            if (owner.activeRun?.completion === completion) owner.activeRun = null;
            if (posSizingSyncInFlightRef.current === completion) posSizingSyncInFlightRef.current = false;
            if (isCurrent()) {
                const next = owner.pending;
                present(() => runtimeRef.current.setPendingPosSizingAutoSync(next));
                if (!next && owner.overviewRefreshPending) {
                    owner.overviewRefreshPending = false;
                    if (!queued.allowParentPublication && !runtimeRef.current.importApplyInFlightRef?.current) {
                        // This display helper cannot strand an already-finished
                        // authority waiter, whether it throws or rejects.
                        try {
                            const refresh = getRiskCommandsOrThrow(runtimeRef.current.riskCommandsRef,
                                'usePosSizingSync.contextUpdateComplete', ['refreshSelectedOverview'])
                                .refreshSelectedOverview({ reason: queued.reason });
                            void Promise.resolve(refresh).catch(logOverviewRefreshError);
                        } catch (error) { logOverviewRefreshError(error); }
                    }
                }
            }
            // A mandatory caller may release its own parent fence and refresh
            // Overview as soon as this resolves, so release the child fence first.
            if (!followup) finishWaiters(queued, isCurrent() ? result : obsoleteResult());
            if (owner.pending && owner.active) queueMicrotask(() => pumpRef.current?.());
        });
    }, [posSizingSyncInFlightRef, publishState]);
    pumpRef.current = pump;

    const requestPosSizingContextUpdate = useCallback((options = {}) => {
        const owner = scope;
        const epoch = owner.epoch;
        if (!owner.active || !owner.userUid || bootstrapOwnerRef.current !== owner
            || !requestStillCurrent(options)) return Promise.resolve(obsoleteResult());
        return new Promise((resolve) => {
            let prior = owner.pending;
            if (prior && !requestStillCurrent(prior)) {
                finishWaiters(prior, obsoleteResult());
                prior = null;
            }
            const finish = result => resolve(owner.active && owner.epoch === epoch
                && bootstrapOwnerRef.current === owner && requestStillCurrent(options) ? result : obsoleteResult());
            const acceptance = prior?.acceptance === 'actual_change' || options.acceptance === 'actual_change'
                ? 'actual_change' : prior?.acceptance === 'manual' || options.acceptance === 'manual' ? 'manual' : 'automatic';
            const next = {
                ...prior, ...options, acceptance,
                reason: options.reason ?? prior?.reason ?? 'parent_context_check',
                reasons: [...new Set([...(prior?.reasons ?? []), options.reason ?? 'parent_context_check'])],
                requestId: prior?.requestId ?? createRequestId(),
                remainingFollowups: prior?.remainingFollowups ?? 1,
                checkOnly: prior ? prior.checkOnly === true && options.checkOnly === true
                    : options.checkOnly === true,
                requireRebuild: Boolean(prior?.requireRebuild || options.requireRebuild),
                allowParentPublication: Boolean(prior?.allowParentPublication || options.allowParentPublication),
                waiters: [...(prior?.waiters ?? []), finish],
            };
            owner.pending = next;
            const present = apply => next.stagePublicationState
                ? next.stagePublicationState(apply, () => owner.active && owner.epoch === epoch
                    && bootstrapOwnerRef.current === owner && requestStillCurrent(options)) : apply();
            present(() => runtimeRef.current.setPendingPosSizingAutoSync(next));
            // Set the shared pending state before the status read can expose a
            // stale comparison to the manual prompt.
            if (acceptance !== 'automatic' || runtimeRef.current.posSizingAutoSyncEnabledRef.current) {
                present(() => runtimeRef.current.setPosSizingFreshnessDetailsOpen(false));
            }
            queueMicrotask(() => pumpRef.current?.());
        });
    }, [scope]);

    useEffect(() => {
        if (syncStatusBootstrapEnabled && userUid) {
            void requestPosSizingContextUpdate({ reason: 'workspace_context_ready' });
        }
    }, [actualPortfolioVersion, factorSnapshotDate, posSizingIdioVolSource, requestedFactorDataModel,
        selectedBetaSource, selectedFactorModel, syncStatusBootstrapEnabled, userUid, requestPosSizingContextUpdate]);
    useEffect(() => {
        const previous = lastViewRef.current;
        lastViewRef.current = { userUid, activeView };
        if (!userUid || !syncStatusBootstrapEnabled || activeView !== 'pos-sizing') return;
        if (previous?.userUid === userUid && previous.activeView === 'pos-sizing') return;
        // Entering a previously loaded view must compare its parent too. The
        // draft consumer's hydration decision is separate from reconciliation.
        void requestPosSizingContextUpdate({ reason: 'pos_sizing_view_entered' });
    }, [activeView, requestPosSizingContextUpdate, syncStatusBootstrapEnabled, userUid]);
    useEffect(() => {
        if (autoSyncPreferencesReady && posSizingAutoSyncEnabled) {
            void requestPosSizingContextUpdate({ reason: 'auto_sync_enabled' });
        }
    }, [autoSyncPreferencesReady, posSizingAutoSyncEnabled, requestPosSizingContextUpdate]);
    useEffect(() => { pumpRef.current?.(); }, [posSizingLoading]);

    const handlePosSizingEditingChange = useCallback((editing) => {
        posSizingEditingRef.current = Boolean(editing);
        if (!editing) pumpRef.current?.();
    }, [posSizingEditingRef]);
    const handlePosSizingSavingChange = useCallback((saving) => {
        posSizingSavingRef.current = Boolean(saving);
        setPosSizingTableSaving(Boolean(saving));
        if (!saving) pumpRef.current?.();
    }, [posSizingSavingRef, setPosSizingTableSaving]);
    const handlePosSizingPendingChangesChange = useCallback((pending) => {
        posSizingPendingChangesRef.current = Boolean(pending);
        if (!pending) pumpRef.current?.();
    }, [posSizingPendingChangesRef]);
    const handlePosSizingFreshnessDetailsOpenChange = useCallback((open) => {
        setPosSizingFreshnessDetailsOpen(Boolean(open));
    }, [setPosSizingFreshnessDetailsOpen]);
    const handleSyncPosSizingDraft = useCallback((options = {}) => requestPosSizingContextUpdate({
        reason: 'pos_sizing_sync', acceptance: 'manual', requireRebuild: options.requireRebuild,
    }), [requestPosSizingContextUpdate]);

    return {
        fetchPosSizingSyncStatus, requestPosSizingContextUpdate, contextUpdateState,
        handlePosSizingEditingChange, handlePosSizingSavingChange,
        handlePosSizingPendingChangesChange, handlePosSizingFreshnessDetailsOpenChange,
        handleSyncPosSizingDraft,
    };
}
